import type { LightingAnswers } from '~/types/lightingAdvisor';

export const LIGHTING_ADVISOR_STEP_QUERY_KEY = 'step';

export const LIGHTING_ADVISOR_ANSWER_QUERY_KEYS = [
  'a2s1q1',
  'a2s1q2',
  'a2s1q3',
  'a2s2q1',
  'a2s2q2_length',
  'a2s2q2_width',
  'a2s2q3',
  'a2s2q4',
  'a2s3q1',
  'a2s3q2',
  'a2s3q3',
  'a2s4q0',
  'a2s4q1',
  'a2s5q1',
  'a2s5q1_further',
  'a2s5q2',
  'a2s5q2_basler_series',
  'a2s5q2_basler_camera',
  'a2s5q2_thirdparty_mount',
  'a2s5q2_thirdparty_monocolor'
] as const;

export type LightingAdvisorUrlState = {
  step: number;
  answers: LightingAnswers;
};

function firstQueryValue(raw: unknown): string | null {
  if (Array.isArray(raw)) {
    return firstQueryValue(raw[0]);
  }
  if (raw == null) {
    return null;
  }
  const value = String(raw).trim();
  return value === '' ? null : value;
}

export function parseLightingAdvisorLegacyHash(
  hash: string
): LightingAdvisorUrlState | null {
  const trimmed = hash.trim().replace(/^#/, '');
  if (!trimmed || !trimmed.includes('step=')) {
    return null;
  }

  const params = new URLSearchParams(trimmed.replaceAll(';', '&'));
  const answers: LightingAnswers = {};
  const rawStep = Number(params.get(LIGHTING_ADVISOR_STEP_QUERY_KEY));
  const step =
    Number.isFinite(rawStep) && rawStep >= 1 && rawStep <= 5 ? rawStep : 1;

  try {
    const parsed = JSON.parse(params.get('gf') ?? '{}') as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { step, answers };
    }
    for (const [answerKey, answerValue] of Object.entries(
      parsed as Record<string, unknown>
    )) {
      if (!LIGHTING_ADVISOR_ANSWER_QUERY_KEYS.includes(answerKey as never)) {
        continue;
      }
      if (Array.isArray(answerValue)) {
        answers[answerKey] = answerValue.map(String).join(',');
      } else if (answerValue != null && answerValue !== '') {
        answers[answerKey] = String(answerValue);
      }
    }
  } catch {
    // Invalid legacy answer payloads are ignored.
  }

  return { step, answers };
}

export function readLightingAdvisorUrlState(options: {
  query: Record<string, unknown>;
  hash?: string;
}): LightingAdvisorUrlState {
  const fromHash = options.hash
    ? parseLightingAdvisorLegacyHash(options.hash)
    : null;
  const answers: LightingAnswers = { ...(fromHash?.answers ?? {}) };
  let step = fromHash?.step ?? 1;

  const queryStep = Number(firstQueryValue(options.query.step));
  if (Number.isFinite(queryStep) && queryStep >= 1 && queryStep <= 5) {
    step = queryStep;
  }

  for (const key of LIGHTING_ADVISOR_ANSWER_QUERY_KEYS) {
    const value = firstQueryValue(options.query[key]);
    if (value != null) {
      answers[key] = value;
    }
  }

  return { step, answers };
}

export function buildLightingAdvisorQuery(
  step: number,
  answers: LightingAnswers
): Record<string, string> {
  const query: Record<string, string> = {
    [LIGHTING_ADVISOR_STEP_QUERY_KEY]: String(step)
  };
  for (const key of LIGHTING_ADVISOR_ANSWER_QUERY_KEYS) {
    const value = answers[key];
    if (value != null && String(value).trim() !== '') {
      query[key] = String(value);
    }
  }
  return query;
}

export function buildLightingAdvisorRouteQuery(
  currentQuery: Record<string, unknown>,
  step: number,
  answers: LightingAnswers
): Record<string, unknown> {
  const query = { ...currentQuery };
  delete query[LIGHTING_ADVISOR_STEP_QUERY_KEY];
  for (const key of LIGHTING_ADVISOR_ANSWER_QUERY_KEYS) {
    delete query[key];
  }
  return Object.assign(query, buildLightingAdvisorQuery(step, answers));
}
